import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { subscribeArtCreditsSync } from '../../utils/artCreditsSync';
import './ArtLimitProgress.css';

const getProgressTone = (progressPercent) => {
  if (progressPercent >= 100) return 'danger';
  if (progressPercent >= 80) return 'warning';
  return 'default';
};

const COLLAPSED_RING_COLORS = {
  default: { track: '#F1D3FF', fill: '#BC00EE', text: '#9002CD' },
  warning: { track: 'rgba(217, 138, 0, 0.2)', fill: 'rgba(217, 138, 0, 1)', text: 'rgba(217, 138, 0, 1)' },
  danger: { track: 'rgba(204, 0, 14, 0.12)', fill: 'rgba(204, 0, 14, 1)', text: 'rgba(204, 0, 14, 1)' },
};

const ArtLimitProgress = ({ artesCriadas = 0, limiteArtes = 0, layout = 'sidebar', checkoutPath = '/PlanCheckoutUser' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSyncingCredits, setIsSyncingCredits] = useState(false);

  useEffect(() => subscribeArtCreditsSync(setIsSyncingCredits), []);

  const safeArtesCriadas = Number.isFinite(Number(artesCriadas)) ? Math.max(0, Number(artesCriadas)) : 0;
  const safeLimiteArtes = Number.isFinite(Number(limiteArtes)) ? Math.max(0, Number(limiteArtes)) : 0;
  const progressPercent = safeLimiteArtes > 0
    ? Math.min(100, (safeArtesCriadas / safeLimiteArtes) * 100)
    : 0;
  const progressTone = getProgressTone(progressPercent);

  if (layout === 'collapsed') {
    const radius = 14;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (progressPercent / 100) * circumference;
    const ringColors = COLLAPSED_RING_COLORS[progressTone];

    return (
      <button
        type="button"
        className={`art-limit-collapsed-ring art-limit-collapsed-ring--${progressTone}`}
        aria-label={t('art_limit_progress.arts_created_count', { created: safeArtesCriadas, total: safeLimiteArtes })}
        title={t('art_limit_progress.arts_created_count', { created: safeArtesCriadas, total: safeLimiteArtes })}
        onClick={() => navigate(checkoutPath)}
      >
        <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
          <circle
            cx="20"
            cy="20"
            r={radius}
            fill="none"
            stroke={ringColors.track}
            strokeWidth="3"
          />
          <circle
            cx="20"
            cy="20"
            r={radius}
            fill="none"
            stroke={ringColors.fill}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 20 20)"
          />
        </svg>
        <span
          className="art-limit-collapsed-percent"
          style={{ color: ringColors.text }}
        >
          {Math.round(progressPercent)}%
        </span>
      </button>
    );
  }

  return (
    <div className={`art-limit-progress-container layout-${layout}`}>
      {layout === 'sidebar' && (
        <div className="art-limit-section-header">
          <span className="art-limit-section-title">{t('art_limit_progress.section_title')}</span>
          <button
            className="art-limit-plus-btn art-limit-plus-btn-header"
            aria-label={t('art_limit_progress.buy_credits')}
            onClick={() => navigate(checkoutPath)}
            type="button"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M10.5 4.5H7.5V1.5C7.5 0.67125 6.82837 0 6 0C5.17163 0 4.5 0.67125 4.5 1.5V4.5H1.5C0.671625 4.5 0 5.17125 0 6C0 6.82875 0.671625 7.5 1.5 7.5H4.5V10.5C4.5 11.3287 5.17163 12 6 12C6.82837 12 7.5 11.3287 7.5 10.5V7.5H10.5C11.3284 7.5 12 6.82875 12 6C12 5.17125 11.3284 4.5 10.5 4.5Z" fill="white" />
            </svg>
            <span className="art-limit-plus-label">{t('art_limit_progress.buy_credits')}</span>
          </button>
        </div>
      )}

      {layout === 'sidebar' && isSyncingCredits && (
        <span className="art-limit-sync-hint art-limit-sync-hint--sidebar">
          {t('art_limit_progress.syncing_credits')}
        </span>
      )}

      <div className={`art-limit-progress-bar art-limit-progress-bar--${progressTone}`}>
        <div
          className={`art-limit-progress-fill art-limit-progress-fill--${progressTone}`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <div className="art-limit-count-row">
        <span className="art-limit-count-text">
          {t('art_limit_progress.arts_created_count', { created: safeArtesCriadas, total: safeLimiteArtes })}
        </span>
        {isSyncingCredits && layout !== 'sidebar' && (
          <span className="art-limit-sync-hint">
            {t('art_limit_progress.syncing_credits')}
          </span>
        )}
        {layout !== 'sidebar' && (
          <button
            className="art-limit-plus-btn"
            aria-label={t('art_limit_progress.buy_credits')}
            onClick={() => navigate(checkoutPath)}
            type="button"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M10.5 4.5H7.5V1.5C7.5 0.67125 6.82837 0 6 0C5.17163 0 4.5 0.67125 4.5 1.5V4.5H1.5C0.671625 4.5 0 5.17125 0 6C0 6.82875 0.671625 7.5 1.5 7.5H4.5V10.5C4.5 11.3287 5.17163 12 6 12C6.82837 12 7.5 11.3287 7.5 10.5V7.5H10.5C11.3284 7.5 12 6.82875 12 6C12 5.17125 11.3284 4.5 10.5 4.5Z" fill="white" />
            </svg>
            <span className="art-limit-plus-label">{t('art_limit_progress.buy_credits')}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ArtLimitProgress;
